import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
  Icon,
  Input,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Organization } from 'services/organization';

interface DeleteCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (organization: Organization) => void;
  organization: Organization | null;
}

const DeleteCompanyModal: React.FC<DeleteCompanyModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  organization,
}) => {
  const { t } = useTranslation();
  const [confirmName, setConfirmName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const handleConfirm = () => {
    if (!organization) return;

    if (confirmName !== organization.name) {
      toast({
        title: t('managementSystem.toast.error'),
        description: 'Organization name does not match. Please enter the correct name.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    onConfirm(organization);
    onClose();
    setConfirmName('');
    setIsSubmitting(false);
  };

  const handleClose = () => {
    setConfirmName('');
    onClose();
  };

  // Don't render anything if modal is not open or no organization
  if (!isOpen || !organization) {
    return null;
  }

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={handleClose}
      returnFocusOnClose={false}
      size={{ base: 'sm', lg: 'lg' }}
    >
      <AlertDialogOverlay bg='rgba(16, 18, 24, 0.45)' backdropFilter='blur(3px)'>
        <AlertDialogContent
          pos='absolute'
          top='23%'
          bg='bg.surfaceRaised'
          borderRadius='xl'
          shadow='ds_lg'
        >
          <AlertDialogHeader
            alignItems='center'
            gap={2}
            display='flex'
            color='state.error'
            fontFamily='heading'
            fontSize='lg'
            fontWeight={600}
            borderColor='border.default'
            borderBottomWidth='1px'
          >
            <Icon as={AlertTriangle} boxSize='18px' />
            {t('managementSystem.deleteCompany.title')}
          </AlertDialogHeader>

          <AlertDialogBody>
            <VStack align='stretch' spacing={4}>
              <Flex
                align='center'
                gap={3}
                p={3}
                color='state.error'
                bg='state.errorSoft'
                borderRadius='md'
              >
                <Icon as={AlertTriangle} flexShrink={0} boxSize='18px' />
                <Text fontSize='sm'>{t('managementSystem.deleteCompany.warningMessage')}</Text>
              </Flex>
              <Text color='text.default'>
                {t('managementSystem.deleteCompany.confirmMessage')}{' '}
                <Text as='span' color='state.error' fontWeight={600}>
                  {organization.name}
                </Text>
                ?
              </Text>
              <Input
                color='text.default'
                bg='bg.surface'
                borderColor='border.strong'
                borderRadius='md'
                _focus={{
                  borderColor: 'state.error',
                  boxShadow: '0 0 0 3px var(--chakra-colors-state-errorSoft)',
                }}
                _placeholder={{ color: 'text.subtle' }}
                onChange={(e) => setConfirmName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleConfirm()}
                placeholder={t('managementSystem.deleteCompany.placeholder', {
                  name: organization.name,
                })}
                value={confirmName}
              />
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter gap={3} borderColor='border.default' borderTopWidth='1px'>
            <Button ref={cancelRef} onClick={handleClose} size='ds-md' variant='ds-ghost'>
              {t('managementSystem.deleteCompany.cancel')}
            </Button>
            <Button
              isDisabled={confirmName !== organization.name}
              isLoading={isSubmitting}
              leftIcon={<Icon as={Trash2} boxSize='16px' />}
              onClick={handleConfirm}
              size='ds-md'
              variant='ds-danger'
            >
              {t('managementSystem.deleteCompany.deleteButton')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default DeleteCompanyModal;
